/**
 * Thin wrapper over the django-allauth "headless" browser API.
 *
 * Covers session probing, password login, MFA verification/enrollment,
 * password reset/change, and logout. Every call sets the `csrftoken` cookie
 * server-side, so a session probe on boot arms CSRF for mutating requests.
 */
import { readCookie } from "./client";

const BASE = "/_allauth/browser/v1";

/** Authenticated user as reported by allauth. */
export interface AllauthUser {
  id: string | number;
  email: string;
  display?: string;
  has_usable_password?: boolean;
}

/** A pending authentication flow (e.g. MFA). */
export interface AllauthFlow {
  id: string;
  is_pending?: boolean;
}

/** Enrolled MFA authenticator entry from allauth. */
export interface MfaAuthenticator {
  type: "totp" | "recovery_codes" | "webauthn";
  created_at: number;
  last_used_at: number | null;
  total_code_count?: number;
  unused_code_count?: number;
  unused_codes?: string[];
}

/** TOTP enrollment provisioning metadata returned when TOTP is not yet active. */
export interface TotpSetupMeta {
  secret: string;
  totp_url: string;
}

/** Generic allauth response envelope. */
export interface AllauthResponse {
  status: number;
  data?: unknown;
  meta?: {
    is_authenticated?: boolean;
    session_token?: string;
    secret?: string;
    totp_url?: string;
  };
  errors?: { message: string; code: string; param?: string }[];
}

/** Error raised when a login or account operation is rejected. */
export class AuthError extends Error {
  readonly code: string;
  readonly pendingFlows: string[];

  constructor(message: string, code: string, pendingFlows: string[] = []) {
    super(message);
    this.name = "AuthError";
    this.code = code;
    this.pendingFlows = pendingFlows;
  }
}

/** Build request headers including the CSRF token and tenant when present. */
function headers(tenant?: string, extra?: Record<string, string>): HeadersInit {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...extra,
  };
  if (tenant) {
    h["X-Tenant"] = tenant;
  }
  const csrf = readCookie("csrftoken");
  if (csrf) {
    h["X-CSRFToken"] = csrf;
  }
  return h;
}

/** Parse an allauth JSON body defensively. */
async function parse(res: Response): Promise<AllauthResponse> {
  try {
    return (await res.json()) as AllauthResponse;
  } catch {
    return { status: res.status };
  }
}

function extractAuthData(data: unknown): { user?: AllauthUser; flows?: AllauthFlow[] } {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data;
  }
  return {};
}

function throwFromResponse(
  body: AllauthResponse,
  fallbackMessage: string,
  fallbackCode: string,
): never {
  const authData = extractAuthData(body.data);
  const flows = (authData.flows ?? []).filter((f) => f.is_pending).map((f) => f.id);
  const first = body.errors?.[0];
  throw new AuthError(
    first?.message ?? (flows.length ? "Additional verification required" : fallbackMessage),
    first?.code ?? (flows.length ? "flow_pending" : fallbackCode),
    flows,
  );
}

/** Result of probing the current allauth browser session. */
export interface SessionState {
  user: AllauthUser | null;
  pendingFlows: string[];
}

/** Probe the current session alongside any pending authentication flows (e.g. MFA after SSO). */
export async function fetchSessionState(): Promise<SessionState> {
  const res = await fetch(`${BASE}/auth/session`, { credentials: "include", headers: headers() });
  const body = await parse(res);
  const authData = extractAuthData(body.data);
  if (res.ok && body.meta?.is_authenticated && authData.user) {
    return { user: authData.user, pendingFlows: [] };
  }
  const flows = (authData.flows ?? []).filter((f) => f.is_pending).map((f) => f.id);
  return { user: null, pendingFlows: flows };
}

/** Probe the current session. Resolves to the user or `null` when anonymous. */
export async function fetchSession(): Promise<AllauthUser | null> {
  const state = await fetchSessionState();
  return state.user;
}

/** Submit a browser redirect form to allauth headless `/auth/provider/redirect` for SSO. */
export function redirectToSsoProvider(providerId: string, callbackUrl: string): void {
  const doc = globalThis.document;
  if (!doc) {
    return;
  }
  const form = doc.createElement("form");
  form.method = "POST";
  form.action = `${BASE}/auth/provider/redirect`;
  const fields: Record<string, string> = {
    provider: providerId,
    callback_url: callbackUrl,
    process: "login",
  };
  const csrf = readCookie("csrftoken");
  if (csrf) {
    fields.csrfmiddlewaretoken = csrf;
  }
  for (const [name, value] of Object.entries(fields)) {
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  doc.body.appendChild(form);
  form.submit();
}

/** Log in with tenant slug, email + password. Throws `AuthError` on rejection or pending MFA. */
export async function login(tenant: string, email: string, password: string): Promise<AllauthUser> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: headers(tenant),
    body: JSON.stringify({ email, password }),
  });
  const body = await parse(res);
  const authData = extractAuthData(body.data);
  if (res.ok && authData.user) {
    return authData.user;
  }
  throwFromResponse(body, "Login failed", "login_failed");
}

/** Complete an MFA challenge during login with a TOTP or recovery code. */
export async function authenticateMfa(tenant: string, code: string): Promise<AllauthUser> {
  const res = await fetch(`${BASE}/auth/2fa/authenticate`, {
    method: "POST",
    credentials: "include",
    headers: headers(tenant),
    body: JSON.stringify({ code: code.trim() }),
  });
  const body = await parse(res);
  const authData = extractAuthData(body.data);
  if (res.ok && authData.user) {
    return authData.user;
  }
  throwFromResponse(body, "Invalid verification code", "incorrect_code");
}

/** Request a password-reset email for `email`. */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/password/request`, {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify({ email: email.trim() }),
  });
  if (!res.ok) {
    const body = await parse(res);
    throwFromResponse(body, "Could not request password reset", "reset_request_failed");
  }
}

/** Reset password using a key from the reset email. */
export async function resetPasswordFromKey(key: string, password: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/password/reset`, {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify({ key: key.trim(), password }),
  });
  // Allauth returns 200 (logged in) or 401 (password reset succeeded, please log in)
  if (res.ok || res.status === 401) {
    const body = await parse(res);
    if (!body.errors?.length) {
      return;
    }
    throwFromResponse(body, "Password reset failed", "reset_failed");
  }
  const body = await parse(res);
  throwFromResponse(body, "Password reset failed", "reset_failed");
}

/** Change or set the password of the currently authenticated user. */
export async function changePassword(
  currentPassword: string | undefined,
  newPassword: string,
): Promise<void> {
  const payload: Record<string, string> = { new_password: newPassword };
  if (currentPassword) {
    payload.current_password = currentPassword;
  }
  const res = await fetch(`${BASE}/account/password/change`, {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await parse(res);
    throwFromResponse(body, "Could not update password", "password_change_failed");
  }
}

/** List enrolled MFA authenticators for the signed-in user. */
export async function listAuthenticators(): Promise<MfaAuthenticator[]> {
  const res = await fetch(`${BASE}/account/authenticators`, {
    credentials: "include",
    headers: headers(),
  });
  const body = await parse(res);
  if (res.ok && Array.isArray(body.data)) {
    return body.data as MfaAuthenticator[];
  }
  return [];
}

/** Fetch TOTP status or provisioning secret + otpauth URI when not yet enrolled. */
export async function getTotpSetup(): Promise<
  { active: true; authenticator: MfaAuthenticator } | { active: false; setup: TotpSetupMeta }
> {
  const res = await fetch(`${BASE}/account/authenticators/totp`, {
    credentials: "include",
    headers: headers(),
  });
  const body = await parse(res);
  if (res.ok && body.data && typeof body.data === "object") {
    return { active: true, authenticator: body.data as MfaAuthenticator };
  }
  if (res.status === 404 && body.meta?.secret && body.meta.totp_url) {
    return {
      active: false,
      setup: { secret: body.meta.secret, totp_url: body.meta.totp_url },
    };
  }
  throwFromResponse(body, "Could not load authenticator setup", "totp_setup_failed");
}

/** Activate TOTP using the 6-digit verification code from the user's authenticator app. */
export async function activateTotp(code: string): Promise<MfaAuthenticator> {
  const res = await fetch(`${BASE}/account/authenticators/totp`, {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify({ code: code.trim() }),
  });
  const body = await parse(res);
  if (res.ok && body.data && typeof body.data === "object") {
    return body.data as MfaAuthenticator;
  }
  throwFromResponse(body, "Invalid authenticator code", "totp_activate_failed");
}

/** Deactivate TOTP for the signed-in user. */
export async function deactivateTotp(): Promise<void> {
  const res = await fetch(`${BASE}/account/authenticators/totp`, {
    method: "DELETE",
    credentials: "include",
    headers: headers(),
  });
  if (!res.ok) {
    const body = await parse(res);
    throwFromResponse(
      body,
      "Could not disable two-factor authentication",
      "totp_deactivate_failed",
    );
  }
}

/** Retrieve or regenerate recovery codes for the signed-in user. */
export async function getOrGenerateRecoveryCodes(
  regenerate = false,
): Promise<MfaAuthenticator | null> {
  const res = await fetch(`${BASE}/account/authenticators/recovery-codes`, {
    method: regenerate ? "POST" : "GET",
    credentials: "include",
    headers: headers(),
  });
  if (res.status === 404) {
    return null;
  }
  const body = await parse(res);
  if (res.ok && body.data && typeof body.data === "object") {
    return body.data as MfaAuthenticator;
  }
  throwFromResponse(body, "Could not load recovery codes", "recovery_codes_failed");
}

/** Terminate the current session. Always resolves (allauth answers 401 once logged out). */
export async function logout(): Promise<void> {
  await fetch(`${BASE}/auth/session`, {
    method: "DELETE",
    credentials: "include",
    headers: headers(),
  });
}
